import { computed, DestroyRef, Injectable, Signal, signal } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, shareReplay, switchMap } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CRMApiService, CrmObject, CrmObjectSearch, ListCrmObjectsRequest } from '@vendasta/crm';
import { CrmFieldService, CrmObjectDisplayService, StandardExternalIds } from '@galaxy/crm/static';
import { ContactData } from './business-contact-selector-modal.component';

const PAGE_SIZE = 20;
export interface DialogData {
  accountGroupID: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContactSelectorModalService {
  private readonly nextCursor$$ = new BehaviorSubject<string>('');
  private readonly searchFormFieldValue$$ = new BehaviorSubject<string>('');
  private readonly loadMore$$ = new BehaviorSubject<boolean>(false);
  private readonly loadMoreTrigger$ = this.loadMore$$.asObservable().pipe(map(() => null));
  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  public readonly loading = toSignal(this.loading$$);
  private readonly contacts$$ = new BehaviorSubject<ContactData[]>([]);
  public readonly contacts = toSignal(this.contacts$$);
  private readonly hasMore$$ = new BehaviorSubject<boolean>(false);
  public readonly hasMore = toSignal(this.hasMore$$);
  private _loadingFirstPage = signal(true);
  public readonly loadingFirstPage: Signal<boolean> = computed(() => this._loadingFirstPage());

  private previousSearchTerm = '';
  public data: DialogData;

  constructor(
    private readonly crmService: CRMApiService,
    private readonly crmFieldService: CrmFieldService,
    private readonly objectDisplayService: CrmObjectDisplayService,
    private readonly destroyRef: DestroyRef,
  ) {}

  public fetchContacts(): void {
    combineLatest([this.searchFormFieldValue$$, this.loadMoreTrigger$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        distinctUntilChanged(),
        shareReplay(1),
        switchMap(([searchTerm]) => {
          this.loading$$.next(true);
          let cursor = this.nextCursor$$.value;
          if (this.previousSearchTerm !== searchTerm) {
            cursor = '';
            this.nextCursor$$.next('');
            this._loadingFirstPage.set(true);
            this.contacts$$.next([]);
          }
          this.previousSearchTerm = searchTerm;

          const request = {
            namespace: this.data.accountGroupID,
            search: { searchTerm } as CrmObjectSearch,
            pagingOptions: { pageSize: PAGE_SIZE, cursor },
          } as ListCrmObjectsRequest;

          return this.crmService.listContacts(request);
        }),
      )
      .subscribe({
        next: (response) => {
          const contacts =
            (response?.crmObjects?.map((contact: CrmObject) => ({
              name: this.getNameForContact(contact),
              email: this.getEmailForContact(contact),
              accountGroupID: this.data.accountGroupID,
              contactID: contact.crmObjectId,
            })) as ContactData[]) ?? [];
          this.contacts$$.next(this.contacts$$.value.concat(contacts));
          this.hasMore$$.next(response.pagingMetadata.hasMore);
          this.nextCursor$$.next(response.pagingMetadata.nextCursor);
          this.loading$$.next(false);
          this._loadingFirstPage.set(false);
        },
        error: () => {
          this.loading$$.next(false);
          this._loadingFirstPage.set(false);
        },
      });
  }

  getNameForContact(contact: CrmObject): string {
    return this.objectDisplayService.getObjectDisplayName('Contact', contact);
  }

  getEmailForContact(contact: CrmObject): string {
    const email = this.crmFieldService.getFieldValueFromCrmObject(contact, StandardExternalIds.Email);
    return email?.stringValue;
  }

  set searchFormFieldValue(value: string) {
    this.searchFormFieldValue$$.next(value);
  }

  set loadMore(value: boolean) {
    this.loadMore$$.next(value);
  }

  public resetState(): void {
    this.searchFormFieldValue = '';
    this.contacts$$.next([]);
    this.hasMore$$.next(false);
    this.loadMore$$.next(false);
    this.loading$$.next(false);
    this.nextCursor$$.next('');
    this.previousSearchTerm = '';
  }
}
